import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseAPI } from '../../../config';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import {
  USER_ROLES,
  ROLE_LABELS,
  TEAM_ROLES,
  PARTNER_ROLES,
  isPartnerRole,
} from '@claimup/shared/constants/roles';
import userStore from '../../../store/userStore';
import ApiKeysManager from '../../../components/ApiKeysManager';
import OperatorsManager from '../../../components/operators-manager/OperatorsManager';
import GestorPermissions from '../../../components/operators-manager/GestorPermissions';
import ArPricingBlock from '../../../components/ar-pricing/ArPricingBlock';
import RecoveryPricingBlock from '../../../components/recovery-pricing/RecoveryPricingBlock';
import TeamBillingAccess from '../../../components/operators-manager/TeamBillingAccess';
import TeamPaymentPlansAccess from '../../../components/operators-manager/TeamPaymentPlansAccess';
import TaskNotifications from '../../../components/operators-manager/TaskNotifications';
import CompanyProfileManager from '../../../components/company-profile/CompanyProfileManager';
import CompanyPhonesBlock from '../../../components/company-phones/CompanyPhonesBlock';
import CallPhonesSection from '../../../components/company-phones/CallPhonesSection';
import CompanyPromptsCard from '../../../components/company-prompts/CompanyPromptsCard';
import {
  ALL_COMPANY_SERVICE_TYPES,
  COMPANY_SERVICE_TYPE_LABELS,
  COMPANY_SERVICE_TYPES,
} from '@claimup/shared/constants/company';
import CompaniesManager from '../../../components/companies-manager/CompaniesManager';
import {
  UserIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PlusIcon,
  TrashIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

// Roles de equipo y de despacho: ven sus datos personales, no los cambian todos.
const TEAM_LIKE_ROLES = [...TEAM_ROLES, ...PARTNER_ROLES];
const PERSONAL_ROLES = ['admin', 'user', 'client', 'company', ...TEAM_LIKE_ROLES];

const initialUserData = {
  username: '',
  userlastname: '',
  usermail: '',
  userphone: '',
  invoicedataname: '',
  invoicedataaddress: '',
  invoicedatacity: '',
  invoicedatacountry: '',
  invoicedatazip: '',
  invoicedatacif: '',
  invoicedataemail: '',
  judicialPercentage: '',
  extrajudicialPercentage: '',
  api_enabled: false,
  emailTemplates: {
    default: [],
    customFields: [],
  },
};

const AccountPanel = ({ userId }) => {
  const navigate = useNavigate();
  const { user: currentUser } = userStore();
  const [userData, setUserData] = useState(initialUserData);
  // Perfil de empresa, Comunicación y Correo son de AR: una empresa sin AR no
  // los ve en su perfil. El admin sí, que los configura también para recobro.
  const seesArBlocks =
    currentUser?.role === 'admin' || userData?.service_type === COMPANY_SERVICE_TYPES.ACCOUNT_RECEIVABLE;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newCustomField, setNewCustomField] = useState({ fieldName: '', templates: [''] });

  useEffect(() => {
    if (!userId) {
      toast.error('ID de usuario no válido', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/dashboard');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sesión no válida', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/login');
      return;
    }

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      axios
        .get(`${baseAPI}/users/${userId}`, { headers })
        .then(response => {
          const responseData = response.data || {};
          setUserData({
            ...initialUserData,
            ...responseData,
            emailTemplates: {
              default: responseData.emailTemplates?.default || [],
              customFields: responseData.emailTemplates?.customFields || [],
            },
          });
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error fetching user data:', error);
          toast.error('Error al cargar los datos del usuario', {
            position: 'top-right',
            autoClose: 3000,
          });
          setIsLoading(false);
          if (error.response?.status === 401) {
            navigate('/login');
          }
        });
    } catch (error) {
      console.error('Error al decodificar el token:', error);
      toast.error('Error de autenticación', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/login');
    }
  }, [userId, navigate]);

  const onChange = e => {
    setUserData(prevData => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  // service_type es un enum sin '': no enviarlo si está vacío para no romper la
  // validación al guardar usuarios que no son company.
  const buildUserPayload = (overrides = {}) => {
    const payload = { ...userData, ...overrides };
    if (!payload.service_type) delete payload.service_type;
    return payload;
  };

  const onSubmit = async e => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      await axios.post(`${baseAPI}/users/update/${userId}`, buildUserPayload(), { headers });

      toast.success('¡Datos actualizados correctamente!', {
        position: 'top-right',
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Error updating user data:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar los datos. Por favor, inténtalo de nuevo.', {
        position: 'top-right',
        autoClose: 3000,
      });
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleApiToggle = async () => {
    const newApiEnabled = !userData.api_enabled;
    setUserData(prev => ({ ...prev, api_enabled: newApiEnabled }));

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      await axios.post(`${baseAPI}/users/update/${userId}`, buildUserPayload({ api_enabled: newApiEnabled }), { headers });

      toast.success('Acceso API actualizado correctamente', {
        position: 'top-right',
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Error updating API access:', error);
      // Revert the toggle if the save fails
      setUserData(prev => ({ ...prev, api_enabled: !newApiEnabled }));
      toast.error(
        error.response?.data?.message || 'Error al actualizar el acceso API. Por favor, inténtalo de nuevo.',
        {
          position: 'top-right',
          autoClose: 3000,
        }
      );
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  // Funciones para manejar templates por defecto
  const handleDefaultTemplateChange = (index, value) => {
    const newTemplates = [...userData.emailTemplates.default];
    newTemplates[index] = value;
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        default: newTemplates,
      },
    });
  };

  const addDefaultTemplate = () => {
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        default: [...userData.emailTemplates.default, ''],
      },
    });
  };

  const removeDefaultTemplate = index => {
    const newTemplates = userData.emailTemplates.default.filter((_, i) => i !== index);
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        default: newTemplates,
      },
    });
  };

  // Funciones para manejar campos personalizados
  const handleCustomFieldChange = (fieldIndex, templateIndex, value, isFieldName = false) => {
    const newCustomFields = [...userData.emailTemplates.customFields];
    if (isFieldName) {
      newCustomFields[fieldIndex].fieldName = value;
    } else {
      newCustomFields[fieldIndex].templates[templateIndex] = value;
    }
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        customFields: newCustomFields,
      },
    });
  };

  const addCustomField = () => {
    if (newCustomField.fieldName.trim() === '') {
      toast.error('El nombre del campo no puede estar vacío', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        customFields: [...userData.emailTemplates.customFields, { ...newCustomField }],
      },
    });
    setNewCustomField({ fieldName: '', templates: [''] });
  };

  const removeCustomField = index => {
    const newCustomFields = userData.emailTemplates.customFields.filter((_, i) => i !== index);
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        customFields: newCustomFields,
      },
    });
  };

  const addTemplateToCustomField = fieldIndex => {
    const newCustomFields = [...userData.emailTemplates.customFields];
    newCustomFields[fieldIndex].templates.push('');
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        customFields: newCustomFields,
      },
    });
  };

  const removeTemplateFromCustomField = (fieldIndex, templateIndex) => {
    const newCustomFields = [...userData.emailTemplates.customFields];
    newCustomFields[fieldIndex].templates = newCustomFields[fieldIndex].templates.filter((_, i) => i !== templateIndex);
    setUserData({
      ...userData,
      emailTemplates: {
        ...userData.emailTemplates,
        customFields: newCustomFields,
      },
    });
  };

  // Antes solo admin/user/client/company veían estos campos: un operador, un
  // gestor o un despacho entraban en «Mi perfil» y el formulario salía vacío.
  // El email lo ven pero no lo cambian: es con lo que entran.
  const userFields = [
    { id: 'grid-username', label: 'Nombre', name: 'username', permissions: PERSONAL_ROLES },
    { id: 'grid-userlastname', label: 'Apellido', name: 'userlastname', permissions: PERSONAL_ROLES },
    { id: 'grid-usermail', label: 'Email', name: 'usermail', permissions: PERSONAL_ROLES, readOnlyFor: TEAM_LIKE_ROLES },
    { id: 'grid-userphone', label: 'Teléfono', name: 'userphone', permissions: PERSONAL_ROLES },
  ];

  const invoiceFields = [
    {
      id: 'grid-invoicename',
      label: 'Nombre de facturación',
      name: 'invoicedataname',
      permissions: ['admin', 'user', 'client', 'company'],
    },
    {
      id: 'grid-invoiceaddress',
      label: 'Dirección',
      name: 'invoicedataaddress',
      permissions: ['admin', 'user', 'client', 'company'],
    },
    {
      id: 'grid-invoicecity',
      label: 'Ciudad',
      name: 'invoicedatacity',
      permissions: ['admin', 'user', 'client', 'company'],
    },
    {
      id: 'grid-invoicezip',
      label: 'Código Postal',
      name: 'invoicedatazip',
      permissions: ['admin', 'user', 'client', 'company'],
    },
    {
      id: 'grid-invoicecountry',
      label: 'País',
      name: 'invoicedatacountry',
      permissions: ['admin', 'user', 'client', 'company'],
    },
    {
      id: 'grid-invoicecif',
      label: 'CIF/NIF',
      name: 'invoicedatacif',
      permissions: ['admin', 'user', 'client', 'company'],
    },
    {
      id: 'grid-invoiceemail',
      label: 'Email de facturación',
      name: 'invoicedataemail',
      permissions: ['admin', 'user', 'client', 'company'],
    },
    { id: 'grid-judicialPercentage', label: 'Porcentaje Judicial', name: 'judicialPercentage', permissions: ['admin'] },
    {
      id: 'grid-extrajudicialPercentage',
      label: 'Porcentaje Extrajudicial',
      name: 'extrajudicialPercentage',
      permissions: ['admin'],
    },
  ];

  const renderFields = fields => {
    if (!Array.isArray(fields)) return null;

    return fields
      .filter(field => field.permissions.includes(currentUser?.role))
      .map(field => (
        <div key={field.id} className='space-y-1.5'>
          <label className='block text-sm font-medium text-slate-700' htmlFor={field.id}>
            {field.label}
          </label>
          <input
            className='block w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 sm:text-sm transition-colors duration-150'
            id={field.id}
            type='text'
            name={field.name}
            value={userData[field.name] || ''}
            onChange={onChange}
            disabled={isLoading || isSaving || (field.readOnlyFor || []).includes(currentUser?.role)}
          />
        </div>
      ));
  };

  // El bloque de facturación solo tiene campos para admin y companies: para el
  // resto saldría una caja vacía.
  const showBilling = invoiceFields.some(field => field.permissions.includes(currentUser?.role));

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Personal Information */}
      <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
        <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
          <div className='flex items-center'>
            <UserIcon className='h-5 w-5 text-blue-600 mr-2' />
            <h3 className='text-lg font-medium text-slate-900'>Información Personal</h3>
          </div>
        </div>
        <div className='p-6'>
          <form onSubmit={onSubmit}>
            <div className='grid gap-6 md:grid-cols-2'>
              {renderFields(userFields)}
              {currentUser?.role === 'admin' && (
                <div className='space-y-1.5'>
                  <label className='block text-sm font-medium text-slate-700' htmlFor='grid-userrole'>
                    Rol
                  </label>
                  <select
                    className='block w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 sm:text-sm transition-colors duration-150'
                    id='grid-userrole'
                    name='userrole'
                    value={userData.userrole}
                    onChange={onChange}
                    disabled={isLoading || isSaving}
                  >
                    {/* De la lista oficial: antes estaba a mano, sin despacho ni
                        abogado (salía «Admin» en sus perfiles) y con un «user»
                        que el modelo no acepta. */}
                    {USER_ROLES.map(role => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role] || role}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {currentUser?.role === 'admin' && userData.userrole === 'company' && (
                <div className='space-y-1.5'>
                  <label className='block text-sm font-medium text-slate-700' htmlFor='grid-service-type'>
                    Tipo de servicio
                  </label>
                  <select
                    className='block w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 sm:text-sm transition-colors duration-150'
                    id='grid-service-type'
                    name='service_type'
                    value={userData.service_type || ''}
                    onChange={onChange}
                    disabled={isLoading || isSaving}
                  >
                    <option value=''>-Sin definir-</option>
                    {ALL_COMPANY_SERVICE_TYPES.map(st => (
                      <option key={st} value={st}>
                        {COMPANY_SERVICE_TYPE_LABELS[st] || st}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* API Toggle Switch - Only visible for admin users */}
            {currentUser?.role === 'admin' && (
              <div className='mt-6 pt-6 border-t border-slate-200'>
                <div className='flex items-center justify-between'>
                  <div>
                    <h4 className='text-sm font-medium text-slate-900'>API Access</h4>
                    <p className='text-sm text-slate-500 mt-1'>Habilita el acceso a la API para este usuario</p>
                  </div>
                  <button
                    type='button'
                    onClick={handleApiToggle}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      userData.api_enabled ? 'bg-blue-600' : 'bg-slate-200'
                    }`}
                    role='switch'
                    aria-checked={userData.api_enabled}
                  >
                    <span className='sr-only'>Enable API access</span>
                    <span
                      aria-hidden='true'
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        userData.api_enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Despacho colaborador: su configuración (clientes, abogados) vive en la
          página Despachos, no aquí. */}
      {currentUser?.role === 'admin' && isPartnerRole(userData?.userrole) && (
        <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
          <div className='p-6 flex items-center justify-between gap-4'>
            <div className='flex items-center'>
              <ScaleIcon className='h-5 w-5 text-blue-600 mr-2' />
              <p className='text-sm text-slate-700'>
                {userData.userrole === 'partner'
                  ? 'Los clientes y los abogados de este despacho se gestionan en la página Despachos.'
                  : 'Los clientes de este abogado se gestionan en la página Despachos, dentro de su despacho.'}
              </p>
            </div>
            <Link
              to='/partners'
              className='shrink-0 px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50'
            >
              Ir a Despachos
            </Link>
          </div>
        </div>
      )}

      {/* Billing Information */}
      {showBilling && (
        <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
          <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
            <div className='flex items-center'>
              <BuildingOfficeIcon className='h-5 w-5 text-blue-600 mr-2' />
              <h3 className='text-lg font-medium text-slate-900'>Datos de Facturación</h3>
            </div>
          </div>
          <div className='p-6'>
            <form onSubmit={onSubmit}>
              <div className='grid gap-6 md:grid-cols-2'>{renderFields(invoiceFields)}</div>
            </form>
          </div>
        </div>
      )}

      {/* Email Templates Section - Only visible for admin */}
      {currentUser?.role === 'admin' && (
        <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
          <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
            <div className='flex items-center'>
              <EnvelopeIcon className='h-5 w-5 text-blue-600 mr-2' />
              <h3 className='text-lg font-medium text-slate-900'>Plantillas de Email</h3>
            </div>
          </div>
          <div className='p-6'>
            <form>
              {/* Default Templates */}
              <div className='mb-6'>
                <div className='flex items-center justify-between mb-2'>
                  <h4 className='text-sm font-medium text-slate-900'>Plantillas por Defecto</h4>
                  <button
                    type='button'
                    onClick={addDefaultTemplate}
                    className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700'
                  >
                    <PlusIcon className='h-4 w-4 mr-1' />
                    Añadir Plantilla
                  </button>
                </div>
                <div className='space-y-2'>
                  {userData.emailTemplates.default.map((template, index) => (
                    <div key={index} className='flex items-center gap-2'>
                      <input
                        type='text'
                        className='flex-1 px-3 py-1.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm transition-colors duration-150'
                        value={template}
                        onChange={e => handleDefaultTemplateChange(index, e.target.value)}
                        placeholder='Escribe tu plantilla aquí...'
                      />
                      <button
                        type='button'
                        onClick={() => removeDefaultTemplate(index)}
                        className='text-red-500 hover:text-red-700'
                      >
                        <TrashIcon className='h-4 w-4' />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Fields */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <h4 className='text-sm font-medium text-slate-900'>Campos Personalizados</h4>
                  <div className='flex items-center gap-2'>
                    <input
                      type='text'
                      value={newCustomField.fieldName}
                      onChange={e => setNewCustomField({ ...newCustomField, fieldName: e.target.value })}
                      placeholder='Nombre del campo'
                      className='px-3 py-1.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm'
                    />
                    <button
                      type='button'
                      onClick={addCustomField}
                      className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700'
                    >
                      <PlusIcon className='h-4 w-4 mr-1' />
                      Añadir Campo
                    </button>
                  </div>
                </div>

                <div className='space-y-4'>
                  {userData.emailTemplates.customFields.map((field, fieldIndex) => (
                    <div key={fieldIndex} className='border border-slate-200 rounded-lg p-3'>
                      <div className='flex items-center justify-between mb-2'>
                        <input
                          type='text'
                          value={field.fieldName}
                          onChange={e => handleCustomFieldChange(fieldIndex, 0, e.target.value, true)}
                          className='text-sm font-medium text-slate-900 px-2 py-1 border-b border-transparent focus:border-blue-500 focus:outline-none'
                        />
                        <div className='flex items-center gap-2'>
                          <button
                            type='button'
                            onClick={() => addTemplateToCustomField(fieldIndex)}
                            className='text-blue-600 hover:text-blue-700'
                          >
                            <PlusIcon className='h-4 w-4' />
                          </button>
                          <button
                            type='button'
                            onClick={() => removeCustomField(fieldIndex)}
                            className='text-red-500 hover:text-red-700'
                          >
                            <TrashIcon className='h-4 w-4' />
                          </button>
                        </div>
                      </div>
                      <div className='space-y-2'>
                        {field.templates.map((template, templateIndex) => (
                          <div key={templateIndex} className='flex items-center gap-2'>
                            <input
                              type='text'
                              className='flex-1 px-3 py-1.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm transition-colors duration-150'
                              value={template}
                              onChange={e => handleCustomFieldChange(fieldIndex, templateIndex, e.target.value)}
                              placeholder='Escribe tu plantilla aquí...'
                            />
                            <button
                              type='button'
                              onClick={() => removeTemplateFromCustomField(fieldIndex, templateIndex)}
                              className='text-red-500 hover:text-red-700'
                            >
                              <TrashIcon className='h-4 w-4' />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Company profile (AI personalization) - la propia company o un admin
          viendo el profile de una company. */}
      {userData?.userrole === 'company' &&
        seesArBlocks &&
        (currentUser?.role === 'admin' || currentUser?.id === userData._id) && (
          <CompanyProfileManager userData={userData} />
        )}

      {/* Users Section - Only visible when viewing a company profile */}
      {userData?.userrole === 'company' && <OperatorsManager userData={userData} />}

      {/* Qué puede hacer su gestor (06-reparto §4.5). El gestor solo existe en AR. */}
      {userData?.userrole === 'company' &&
        userData.service_type === COMPANY_SERVICE_TYPES.ACCOUNT_RECEIVABLE &&
        (currentUser?.role === 'admin' || currentUser?.id === userData._id) && (
          <GestorPermissions companyId={userData._id} isAdmin={currentUser?.role === 'admin'} />
        )}

      {/* Quién del equipo ve la Facturación (docs/facturacion-equipo-plan.md): la
          empresa dueña, o un admin por ella. Toda empresa tiene operadores. */}
      {userData?.userrole === 'company' &&
        (currentUser?.role === 'admin' || currentUser?.id === userData._id) && (
          <TeamBillingAccess
            companyId={userData._id}
            isAdmin={currentUser?.role === 'admin'}
            hasAr={userData.service_type === COMPANY_SERVICE_TYPES.ACCOUNT_RECEIVABLE}
          />
        )}

      {/* Quién del equipo gestiona los planes de pago (docs/planes-de-pago-fase-1-plan.md, §5):
          solo en AR, que es donde su equipo trabaja los expedientes. */}
      {userData?.userrole === 'company' &&
        userData.service_type === COMPANY_SERVICE_TYPES.ACCOUNT_RECEIVABLE &&
        (currentUser?.role === 'admin' || currentUser?.id === userData._id) && (
          <TeamPaymentPlansAccess companyId={userData._id} isAdmin={currentUser?.role === 'admin'} />
        )}

      {/* Avisos de tareas por correo y Slack (docs/avisos-de-tareas-plan.md): la
          empresa dueña, o un admin por ella. */}
      {userData?.userrole === 'company' &&
        (currentUser?.role === 'admin' || currentUser?.id === userData._id) && (
          <TaskNotifications companyId={userData._id} isAdmin={currentUser?.role === 'admin'} />
        )}

      {/* Precios AR (docs/ar-precios-tramos-plan.md): solo el admin, y solo en una
          empresa con AR. La empresa ve en su Facturación AR lo que le toca. */}
      {userData?.userrole === 'company' &&
        userData.service_type === COMPANY_SERVICE_TYPES.ACCOUNT_RECEIVABLE &&
        currentUser?.role === 'admin' && <ArPricingBlock companyId={userData._id} />}

      {/* Precios recobro (docs/facturacion-cliente-plan.md): solo el admin. Toda
          empresa puede tener expedientes en recobro, tenga o no AR. */}
      {userData?.userrole === 'company' && currentUser?.role === 'admin' && (
        <RecoveryPricingBlock companyId={userData._id} />
      )}

      {/* Comunicación: WhatsApp, llamadas y correo de la company. El correo
          (dominio de envío de AR, Resend) es el espejo de la regla del backend
          (canManageCompanyEmail): admin de ClaimUp o el dueño de la company,
          nadie más. Un gestor comunica en AR pero no conecta dominios. */}
      {userData?.userrole === 'company' && seesArBlocks && (
        <CompanyPhonesBlock
          userData={userData}
          showEmail={currentUser?.role === 'admin' || currentUser?.id === userData._id}
          isAdmin={currentUser?.role === 'admin'}
        />
      )}

      {/* El número del gestor: en AR llama él, con el suyo
          (docs/ar-numero-gestor-plan.md). Solo en su propio perfil: el código
          de verificación le llega a él. */}
      {userData?.userrole === 'gestor' && currentUser?.id === userData._id && (
        <div className='bg-white rounded-lg shadow-sm border border-slate-200 p-6'>
          <CallPhonesSection own />
        </div>
      )}

      {/* Acceso a varias empresas. Solo un admin decide qué ve un usuario. */}
      {currentUser?.role === 'admin' && userData?.userrole === 'company' && (
        <CompaniesManager
          userData={userData}
          onUpdated={companyIds =>
            setUserData(prev => ({ ...prev, company_ids: companyIds }))
          }
        />
      )}

      {/* API Keys Section */}
      {(currentUser?.role === 'admin' || (userData?.userrole === 'company' && userData.api_enabled)) && (
        <>
          {/* API Keys Manager */}
          <ApiKeysManager userId={userData._id} />
        </>
      )}

      {/* Prompts de IA específicos de esta company — solo visible/administrable
          por admin; enlaza a la pantalla AI Agent para editar. */}
      {currentUser?.role === 'admin' && userData?.userrole === 'company' && (
        <CompanyPromptsCard userData={userData} />
      )}

      {/* Common Save Button for all users */}
      <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
        <div className='p-6'>
          <form onSubmit={onSubmit}>
            <div className='flex items-center justify-end gap-4'>
              <div className='text-sm text-slate-600 flex items-center'>
                <CheckCircleIcon className='h-5 w-5 text-emerald-500 mr-2' />
                Tus datos están protegidos
              </div>
              <button
                type='submit'
                disabled={isLoading || isSaving}
                className='inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150'
              >
                {isSaving ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                    Guardando cambios...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className='h-4 w-4 mr-2' />
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccountPanel;
